
import Vue from "vue";
import { mailboxApi } from "@/api/mailbox";
import { mapState } from "vuex";
import { Email } from "@/api/email";
import axios from "axios";
import FileDrop from "@/components/FileDrop.vue";
import {Contact, contactApi, ContactSearchRow, CustomerType} from "@/api/contacts";
import { Template } from "@/api/template";
import { Attachment, attachmentApi } from "@/api/attachments";
import { getTinymce } from "@tinymce/tinymce-vue/lib/cjs/main/ts/TinyMCE";
import "tinymce/tinymce";
import "tinymce/themes/silver";
import "tinymce/icons/default";
import "tinymce/skins/ui/oxide/skin.css";
/* Import plugins */
import "tinymce/plugins/advlist";
import "tinymce/plugins/code";
import "tinymce/plugins/emoticons";
import "tinymce/plugins/emoticons/js/emojis";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/table";
import "tinymce/plugins/colorpicker";
import "tinymce/plugins/wordcount";
import QuickContactSearch from "@/views/contacts/QuickContactSearch.vue";
import EventBus from "@/utils/EventBus";
import {findConfirmDialogRef} from "@/utils/RefUtils";
import PdfThumbnail from "@/components/PdfThumbnail.vue";
import {toLocalDateString} from "@/utils/UtcToDate";
import {SharedDocument, sharedDocumentApi} from "@/api/sharedDocument";
import SharedDocuments from "@/views/misc/SharedDocuments.vue";
import {imageGalleryApi} from "@/api/imageGallery";


export interface SendMessageTransfer {
  to: Array<string>;
  cc: Array<string>;
  bcc: Array<string>;
  prefilledSubject: string;
  prefilledContent: string;
  basedOnEmailId: string;
  draft: boolean;
  contact: Contact;
  disableAutoSignature: boolean;
  predefinedDocumentRefs: string[] | null,
  imageGalleryRefs: string[] | null,
  contactDocumentRefs: string[] | null,
}

export default Vue.extend({
  name: "SendMessage",
  components: {
    PdfThumbnail,
    FileDrop,
    editor: () =>
        import(/* webpackChunkName: "tinymce" */ "@tinymce/tinymce-vue"),
    QuickContactSearch,
  },
  props: {
    sendMessageTransfer: {
      type: Object as () => SendMessageTransfer | null,
      required: false,
      default: null
    },
  },
  data() {
    return {
      searchContactDialog: false,
      tab: 0,
      init: true,
      working: false,
      ccMode: true,
      bccMode: true,
      subject: "",
      recipient: "",
      recipientList: [] as string[],
      cc: "",
      ccList: [] as string[],
      bcc: "",
      bccList: [] as string[],
      body: "",
      currFiles: [] as File[],
      files: [] as File[],
      editorSettings: {
        modules: {
          table: true,
        },
      },
      mailboxId: "",
      selectedDocuments: [] as string[],
      selectedPredefinedDocuments: [] as string[],
      selectedImages: [] as string[],
      contactDocuments: [] as Attachment[],
      predefinedDocuments:  [] as SharedDocument[],
      images:  [] as SharedDocument[],
      encounterForContact: "",
      emailAddresses: [] as string[],
      contact: null as Contact | null
    };
  },
  computed: {
    ...mapState("auth", ["currentUser"]),
    ...mapState("template", ["templates"]),
  },
  watch: {
    sendMessageTransfer: {
      immediate: true,
      handler: async function () {
        this.clear();
        [this.emailAddresses, this.predefinedDocuments, this.images] = await Promise.all([
          contactApi.listEmails(),
          sharedDocumentApi.listDocuments(),
          imageGalleryApi.listDocuments()
        ]);
        if (this.sendMessageTransfer) {
          this.recipientList = this.sendMessageTransfer.to ?? [];
          if (this.sendMessageTransfer.contact !== null) {
            this.contactDocuments = await attachmentApi.list(
              this.sendMessageTransfer.contact.id ?? ""
            );
            this.contact = this.sendMessageTransfer.contact;
          }
          if (!this.sendMessageTransfer.disableAutoSignature) {
            this.body = "<br/><p>{{Funkenflug-Signatur}}<p/>";
          }
          if (
            this.sendMessageTransfer.prefilledContent &&
            this.sendMessageTransfer.prefilledContent.length > 1
          ) {
            this.body =
              this.body +
              (this.escapeHtml(this.sendMessageTransfer.prefilledContent) ??
                "");
          }
          this.subject = this.sendMessageTransfer.prefilledSubject;
          this.ccList = this.sendMessageTransfer.cc;
          if (this.ccList && this.ccList.length > 0) {
            this.ccList = this.ccList.filter((cc) => {
              return this.recipientList.findIndex(
                (recipient) => recipient !== cc
              ) === 0
                ? true
                : false;
            });
          }
          this.bccList = this.sendMessageTransfer.bcc;
          if (this.bccList && this.bccList.length > 0) {
            this.bccList = this.bccList.filter((bcc) => {
              return this.recipientList.findIndex(
                (recipient) => recipient !== bcc
              ) === 0
                ? true
                : false;
            });

            this.bccList = this.bccList.filter((bcc) => {
              return this.ccList.findIndex((recipient) => recipient !== bcc) ===
                0
                ? true
                : false;
            });

          }

          this.selectedPredefinedDocuments = this.sendMessageTransfer.predefinedDocumentRefs ?? [];
          this.selectedImages = this.sendMessageTransfer.imageGalleryRefs ?? [];
          this.selectedDocuments = this.sendMessageTransfer.contactDocumentRefs ?? [];
        }else {
          this.body = "<br/><p>{{Funkenflug-Signatur}}</p>";
        }
      },
    },
  },
  async mounted() {
    const mailboxes = await mailboxApi.list();
    if (mailboxes && mailboxes.length > 0) {
      this.mailboxId = mailboxes[0].id ?? "";
    }
  },
  methods: {
    validEmail(values: string[]) {
      return values.every(
        (value) => value.length > 3 && value.indexOf("@") > 1
      );
    },
    close() {
      getTinymce().remove();
      this.$emit("close");
    },
    removeSelectedDocument(item: Attachment | null) {
       if( item != null ) {
         const index = this.selectedDocuments.findIndex(
             (selected: string) => selected === item["id"]
         );
         if (index > -1) {
           this.selectedDocuments.splice(index, 1);
         }
       }
     },
    removeSelectedPredefinedDocument(item: SharedDocument | null) {
      const index = this.selectedPredefinedDocuments.findIndex(
          (selected) => selected === item?.documentRef
      );
      if( index > -1 ) {
        this.selectedPredefinedDocuments.splice(index, 1);
      }
    },
    removeSelectedImage(item: SharedDocument | null) {
      const index = this.selectedImages.findIndex(
          (selected) => selected === item?.documentRef
      );
      if( index > -1 ) {
        this.selectedImages.splice(index, 1);
      }
    },
    escapeHtml(content: string) {
      let modifiedContent = content;
      if (content && content.indexOf("<body") > -1) {
        const indexStart =  content.indexOf(">", content.indexOf("<body")) + 1;
        const indexEnd = content.indexOf("</body>");
        modifiedContent = content.substr(indexStart, indexEnd - indexStart);
      }
      while(modifiedContent.indexOf("<img") > -1 ) {
        modifiedContent = modifiedContent.substring(0, modifiedContent.indexOf("<img")) + modifiedContent.substring(modifiedContent.indexOf(">", modifiedContent.indexOf("<img") + 4) + 1, modifiedContent.length - 1);
      }
      return modifiedContent;
    },
    insertTemplate(template: Template) {
      if (template) {
        let content = template.content;
        this.subject = template.subject;
        this.selectedPredefinedDocuments = template.predefinedDocuments ?? [];
        if (this.contact) {
          const anrede = this.contact.salutation
            ? this.$t(
                "enum.salutation." + this.contact.salutation
              ).toString()
            : "";
          content = content.replaceAll("{{anrede}}", anrede);
          content = content.replaceAll(
            "{{vorname}}",
            this.contact.givenName ?? ""
          );
          content = content.replaceAll(
            "{{nachname}}",
            this.contact.surname ?? ""
          );
        }
        getTinymce().activeEditor.execCommand(
          "mceInsertContent",
          true,
          content
        );
      }
    },
    removeFromDraft(email: Email) {
      if (this.sendMessageTransfer?.draft) {
        const drafts: Email[] = JSON.parse(
          localStorage.getItem("drafts") ?? "[]"
        );
        const index = drafts.findIndex((m) => m.subject === email.subject);
        if (index > -1) {
          drafts.splice(index, 1);
          localStorage.setItem("drafts", JSON.stringify(drafts));
        }
        EventBus.$emit("draftUpdated", "true");
      }
    },
    addToDrafts() {
      let drafts: Email[] = JSON.parse(localStorage.getItem("drafts") ?? "[]");
      const message = {
        recipients: this.recipientList,
        cc: this.ccList,
        bcc: this.bccList,
        subject: this.subject,
        bodyAsHtml: this.body,
        id: this.sendMessageTransfer?.basedOnEmailId,
        predefinedDocumentRefs: this.selectedPredefinedDocuments,
        contactDocumentRefs: this.selectedDocuments,
        imageGalleryRefs: this.selectedImages,
        contactRef: this.sendMessageTransfer?.contact.id ?? null,
      } as Email;

      drafts = drafts.filter((m) => m.subject !== message.subject);
      drafts.push(message);
      localStorage.setItem("drafts", JSON.stringify(drafts));
      (findConfirmDialogRef(this) as any).open(
        this.$t("Entwurf speichern") as string,
        this.$t("In Entwürfe abgelegt / aktualisiert") as string,
        {
          textColor: "green",
          noconfirm: false,
        }
      );
      EventBus.$emit("draftUpdated", "true");
    },
    removeFile(index: number) {
      this.files.splice(index, 1);
    },
    inputFileChanged() {
      this.files = [...this.currFiles, ...this.files];
      this.currFiles = [];
    },
    async sendMessageWithFurtherEncounter(contactSearchRow: ContactSearchRow) {
      this.encounterForContact = contactSearchRow.id ?? "";
      await this.sendEmail();
    },
    async sendMessage() {
      // eslint-disable-next-line
      const recipientOrSupplier = await contactApi.whichRecipientIsSupplierOrReseller(this.recipientList.concat(this.ccList).concat(this.bccList))
      if ( recipientOrSupplier && recipientOrSupplier.length > 0) {
        const res = await (findConfirmDialogRef(this)).open(
            "Empfänger ist Lieferant bzw. Wiederverkäufer",
            `Folgende Empfänger sind Lieferant bzw. Wiederverkäufer: ${recipientOrSupplier}. Ausgehende E-Mail einem weiteren Kontakt als Begegnung hinterlegen?`,
          {
            textColor: "green",
            noconfirm: false,
            textOk: "Ja",
            textCancel: "Nein",
            secondOkButton: false,
          }
        );
        new Promise(resolve => setTimeout(resolve, 500)).then(async () => {
          if (res) {
            this.searchContactDialog = true;
          } else {
            await this.sendEmail();
          }
        });
      } else {
        await this.sendEmail();
      }
    },
    async sendEmail() {
      let dialogText = "Wirklich die E-Mail versenden?";

      if( this.subject == null || this.subject.length === 0) {
         dialogText = dialogText + " Kein Betreff vorhanden!";
      }

      const res = await (findConfirmDialogRef(this)).open(
        this.$t("Achtung") as string,
        this.$t(dialogText) as string,
        {
          textColor: "red",
          noconfirm: false,
          textOk: "Ja",
          textCancel: "Nein und zurück",
          secondOkButton: false,
        }
      );
      this.working = true;

      if (res ) {
        const message = {
          recipients: this.recipientList.map((value) => value.trim()),
          cc: this.ccList.map((value) => value.trim()),
          bcc: this.bccList.map((value) => value.trim()),
          subject: this.subject,
          bodyAsHtml: this.body,
          contactDocumentRefs: this.selectedDocuments,
          predefinedDocumentRefs: this.selectedPredefinedDocuments,
          imageGalleryRefs: this.selectedImages,
        } as Email;
        try {
          await mailboxApi.sendMessage(
            this.mailboxId,
           this.sendMessageTransfer ? this.sendMessageTransfer.basedOnEmailId : null,
            message,
            this.files,
            this.encounterForContact
          );
          this.clear();
          this.removeFromDraft(message);
          this.$emit("MessageSent");
        } finally {
          this.working = false;
          this.encounterForContact = "";
          getTinymce().remove();
          EventBus.$emit("emailWasSend");
          this.contact = null;
        }
      } else {
        this.working = false;
      }
    },
    mapToIcon(attachment: Attachment | SharedDocument | null) {
      if( !attachment) {
        return "mdi-file-outline";
      }
      if (attachment.mimeType && attachment.mimeType.indexOf("image") > -1) {
        return "mdi-file-image";
      }
      if (attachment.mimeType && attachment.mimeType.indexOf("pdf") > -1) {
        return "mdi-file-pdf-box";
      }
      if (attachment.mimeType && attachment.mimeType.indexOf("csv") > -1) {
        return "mdi-file-delimited";
      }
      if (attachment.mimeType && attachment.mimeType.indexOf("excel") > -1) {
        return "mdi-file-excel";
      }

      return "mdi-file-outline";
    },
    formatCurrentAttachmentTitle(file: File) {
      let sizeText = " - ";
      if ((file.size ?? 0) / 1024 > 1024) {
        sizeText +=
            new Intl.NumberFormat().format(
                (file.size ?? 0) / 1024 / 1024
            ) + " MByte";
      } else {
        sizeText +=
            new Intl.NumberFormat().format((file.size ?? 0) / 1024) +
            " KByte";
      }
      return (
          "letzte Aktualisierung " +
          toLocalDateString(new Date(file.lastModified)) +
          sizeText
      );
    },
    formatAttachmentTitle(attachment: Attachment | SharedDocument) {
      if( !attachment) {
        return "";
      }
      let sizeText = " - ";
      if ((attachment.sizeByte ?? 0) / 1024 > 1024) {
        sizeText +=
            new Intl.NumberFormat().format(
                (attachment.sizeByte ?? 0) / 1024 / 1024
            ) + " MByte";
      } else {
        sizeText +=
            new Intl.NumberFormat().format((attachment.sizeByte ?? 0) / 1024) +
            " KByte";
      }
      return (
          "hochgeladen am " +
          (attachment.uploadedAtUtc != undefined ? toLocalDateString(attachment.uploadedAtUtc) : 'Unbekannt') +
          sizeText
      );
    },
    createPreviewUrl(file: File) {
      return URL.createObjectURL(file)
    },
    urlToDownload(attachment: Attachment | SharedDocument | null) {
      if( !attachment) {
        return null;
      }
      if( Object.hasOwn(attachment, "documentRef") ) {
        if( Object.hasOwn(attachment, "imageGallery") ) {
         return  imageGalleryApi.generateDownloadLink(
              {
                documentRef: (attachment as SharedDocument).documentRef ?? "",
                name: attachment.name ?? "",
                mimeType: attachment.mimeType ?? "",
                uploadedAtUtc: new Date(),
                documentClassification: null
              }
          )
        }
        return (
            sharedDocumentApi.generateDownloadLink(
                {
                  documentRef: (attachment as SharedDocument).documentRef ?? "",
                  name: attachment.name ?? "",
                  mimeType: attachment.mimeType ?? "",
                  uploadedAtUtc: new Date(),
                  documentClassification: null
                }
            )
        )
      }
      return (
          attachmentApi.generateDownloadLink(
              this.sendMessageTransfer?.contact.id ?? "",
              (attachment as Attachment)?.id ?? ""
          )
      );
    },
    urlToDownloadForPreview(attachment: Attachment | SharedDocument | null) {
      const url = this.urlToDownload(attachment);
      if( !url) {
        return null;
      }
      return this.urlToDownload(attachment) + "?isPreview=true";
    },
    removeAttachment(attachment: Attachment | SharedDocument | null) {
     if( attachment != null && Object.hasOwn(attachment, "documentRef") ) {
        if( Object.hasOwn(attachment, "imageGallery") ) {
          this.removeSelectedImage(attachment as SharedDocument);
        }else {
          this.removeSelectedPredefinedDocument(attachment as SharedDocument);
        }
      }else  {
        this.removeSelectedDocument(attachment as Attachment);
      }
    },
    downloadAttachment(attachment: Attachment | null) {
      const url = this.urlToDownload(attachment);
      if( !url ) {
        return
      }
      axios({
        url: url,
        method: "GET",
        responseType: "blob",
      }).then((response) => {
        const fileURL = window.URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute("download", attachment?.name ?? "");
        document.body.appendChild(fileLink);
        fileLink.click();
      });
    },
    addFiles(fileList: FileList) {
      for (const item of fileList) {
        // eslint-disable-next-line
        this.currFiles.push(item);
      }
      this.inputFileChanged();
    },
    fetchAttachment(ref: string): Attachment | SharedDocument | null {
      const indexPredefined = this.predefinedDocuments.findIndex(pd => pd.documentRef === ref);
      if( indexPredefined > -1 ) {
        return this.predefinedDocuments[indexPredefined];
      }
      const indexImagePredefined = this.images.findIndex(pd => pd.documentRef === ref);
      if( indexImagePredefined > -1 ) {
        return this.images[indexImagePredefined];
      }
      return this.contactDocuments.find(pd => pd.id === ref) ?? null;
    },
    clear() {
      this.files = [];
      this.contactDocuments = [];
      this.selectedDocuments = [];
      this.selectedPredefinedDocuments = [];
      this.selectedImages = [];
      this.currFiles = [];
      this.bccList = [];
      this.bcc = "";
      this.ccList = [];
      this.cc = "";
      this.recipientList = [];
      this.recipient = "";
      this.body = "";
      this.subject = "";
      this.encounterForContact = "";
    },
    loaded: function (event: any, editor: any) {
      this.editor = editor;
    },
    focusInMe(event: any, editor: any) {
      event.stopImmediatePropagation();
    },
  }
});
